
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { submitEvent, fetchEventCategories } from "@/services/eventService";
import { DateTimeSection } from "./forms/event/DateTimeSection";
import { EventDetailsSection } from "./forms/event/EventDetailsSection";
import { formSchema, FormValues } from "./forms/event/types";

const EventForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['eventCategories'],
    queryFn: fetchEventCategories,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      start_date: new Date(),
      start_time: "09:00",
      end_date: new Date(),
      end_time: "17:00",
      location: "",
      organizer: "",
      website: "",
      category: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const startDateTime = new Date(
        `${format(data.start_date, "yyyy-MM-dd")}T${data.start_time}`
      );
      const endDateTime = new Date(
        `${format(data.end_date, "yyyy-MM-dd")}T${data.end_time}`
      );

      await submitEvent({
        title: data.title,
        description: data.description || "",
        start_date: startDateTime,
        end_date: endDateTime,
        location: data.location || "",
        organizer: data.organizer || "",
        website: data.website || "",
        category: data.category || "",
        image_url: ""
      });
      toast.success("Event submitted successfully!");
      navigate("/events");
    } catch (error) {
      console.error("Error submitting event:", error);
      toast.error("Failed to submit event. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <EventDetailsSection
          form={form}
          categories={categories}
          isCategoriesLoading={isCategoriesLoading}
        />
        
        <DateTimeSection form={form} />

        <Button 
          type="submit" 
          className="w-full bg-eco-500 hover:bg-eco-600" 
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Event"}
        </Button>
      </form>
    </Form>
  );
};

export default EventForm;
